// Browser-side error reporting. Inert without REACT_APP_SENTRY_DSN, so
// local development and any build without one behave exactly as before.
//
// This catches the failures the server never sees: a render crash that
// leaves a provider staring at a blank calendar, a bundle that broke on
// one browser. Those currently reach us only if someone bothers to say
// something.
import * as Sentry from '@sentry/react';

const dsn = process.env.REACT_APP_SENTRY_DSN;

export function initSentry() {
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: process.env.REACT_APP_ENV || 'production',
    release: process.env.REACT_APP_BUILD_VERSION || undefined,

    // Errors only. Session replay and tracing are worth revisiting once
    // there's enough traffic to sample; today they'd mostly cost quota.
    tracesSampleRate: 0,

    // Client records must never ride along with an error report.
    sendDefaultPii: false,

    // Browser-extension and third-party script noise that isn't our bug.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      /^Network request failed$/,
      /Loading chunk \d+ failed/,
    ],

    // Console breadcrumbs carry whatever a component logged, which can be
    // a person's words (the assistant's messages, a client's note). Keep
    // navigation/click/fetch crumbs; never console lines.
    beforeBreadcrumb(crumb) {
      return crumb && crumb.category === 'console' ? null : crumb;
    },

    beforeSend(event) {
      // Strip anything that could carry a name, phone or address out of
      // the URL or breadcrumbs before it leaves the browser.
      if (event.request?.url) {
        event.request.url = event.request.url.split('?')[0];
      }
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map(b => {
          if (b.data?.url) b.data.url = String(b.data.url).split('?')[0];
          return b;
        });
      }
      return event;
    },
  });
}

// Called from AuthContext once we know who's signed in. Id only.
export function setSentryUser(userId) {
  if (!dsn) return;
  Sentry.setUser(userId ? { id: String(userId) } : null);
}

// The client twin of server/utils/observability.reportSilentSkip: a
// handler that quietly declines to do its work is not a crash, so crash
// reporting never sees it. Counting it is what turns "a record path has
// been dead for weeks" into a rising number. Grouped by area + reason so a
// spike is one issue. Never throws; detail must never carry a name, phone,
// email or message text.
export function reportSilentSkip(area, reason, detail = {}) {
  try {
    // eslint-disable-next-line no-console
    console.warn(`[skip:${area}] ${reason}`, detail);
    if (!dsn) return;
    Sentry.withScope(scope => {
      scope.setLevel('warning');
      scope.setTag('area', area);
      scope.setTag('skip_reason', reason);
      scope.setContext('detail', detail);
      scope.setFingerprint(['silent-skip', area, reason]);
      Sentry.captureMessage(`${area} skipped: ${reason}`, 'warning');
    });
  } catch (_) { /* reporting must never break the caller */ }
}

export { Sentry };
